import type { CustomFieldPrefix, PagedBodyParams, SortedBodyParams } from './common';

export type GeneralFilter = Filter<Record<string, Date | boolean | number | string | readonly string[]>>;

type NullableComparisonFilter<T, K extends keyof T> = {
    readonly field: K;
    readonly op: 'eq' | 'neq';
    readonly value: T[K] | null;
};

type ComparisonFilter<T, K extends keyof T> = {
    readonly field: K;
    readonly op: 'eq' | 'ge' | 'gt' | 'le' | 'lt' | 'neq';
    readonly value: T[K];
};

type StringComparisonFilter<T, K extends keyof T> = {
    readonly field: K;
    readonly ignore_case: boolean | undefined;
    readonly op: 'contains' | 'starts_with';
    readonly value: string;
};

type NullFilter<T> = {
    readonly field: keyof T;
    readonly op: 'is_not_null' | 'is_null';
};

type BetweenFilter<T, K extends keyof T> = {
    readonly field: K;
    readonly lower: T[K];
    readonly op: 'between';
    readonly upper: T[K];
};

type ListFilter<T, K extends keyof T> = {
    readonly field: K;
    readonly op: 'in' | 'not_in';
    readonly values: ReadonlyArray<T[K] | null>;
};

type ListFilterByType<T, K extends keyof T, U = unknown> = {
    readonly field: K;
    readonly op: 'in' | 'not_in';
    readonly values: ReadonlyArray<U>;
};

type FilterCriteriaType = 'boolean' | 'date' | 'integer' | 'string';

export type BaseFilterCriteria<K extends string, Value> = {
    readonly field: K;
    readonly type: FilterCriteriaType;
    readonly values?: ReadonlyArray<Value | null>;
};

export type FilterCriteria<T, K extends keyof T> = BaseFilterCriteria<K & string, T[K]>;

export type FilterCriteriaDto<T> = {
    readonly filters?: ReadonlyArray<FilterCriteria<T, keyof T>>;
};

type BaseCustomCriteria<TypeName extends FilterCriteriaType, TypeValue> = {
    readonly field: `${typeof CustomFieldPrefix}${string}`;
    readonly type: TypeName;
    readonly values?: ReadonlyArray<TypeValue | null>;
};

type CustomStringFilterCriteria = BaseCustomCriteria<'string', string>;

type CustomBooleanFilterCriteria = BaseCustomCriteria<'boolean', string>;

type CustomNumberFilterCriteria = BaseCustomCriteria<'integer', number>;

type CustomDateFilterCriteria = BaseCustomCriteria<'date', number>;

export type CustomFilterCriteria =
    | CustomBooleanFilterCriteria
    | CustomDateFilterCriteria
    | CustomNumberFilterCriteria
    | CustomStringFilterCriteria;

export type Filter<T> = CompoundFilter<T> | NonCompoundFilter<T> | NotFilter<T>;

export type CompoundFilter<T> = {
    readonly filters: ReadonlyArray<Filter<T>>;
    readonly op: 'and' | 'or';
};

type NotFilter<T> = {
    readonly filter: Filter<T>;
    readonly op: 'not';
};

export type NonCompoundFilter<T> =
    | BetweenFilter<T, keyof T>
    | ComparisonFilter<T, keyof T>
    | ListFilter<T, keyof T>
    | ListFilterByType<T, keyof T>
    | NullableComparisonFilter<T, keyof T>
    | NullFilter<T>
    | StringComparisonFilter<T, keyof T>;

export const and = <T>(...filters: readonly Filter<T>[]): CompoundFilter<T> => ({ filters, op: 'and' });

export const equals = <T, K extends keyof T>(field: K, value: T[K] | null): NullableComparisonFilter<T, K> => ({
    field,
    op: 'eq',
    value,
});

export const lessOrEqual = <T, K extends keyof T>(field: K, value: T[K]): ComparisonFilter<T, K> => ({
    field,
    op: 'le',
    value,
});
export const greaterOrEqual = <T, K extends keyof T>(field: K, value: T[K]): ComparisonFilter<T, K> => ({
    field,
    op: 'ge',
    value,
});
export const contains = <T, K extends keyof T>(
    field: K,
    value: string,
    ignoreCase?: boolean,
): StringComparisonFilter<T, K> => ({ field, ignore_case: ignoreCase, op: 'contains', value });
export const startsWith = <T, K extends keyof T>(
    field: K,
    value: string,
    ignoreCase?: boolean,
): StringComparisonFilter<T, K> => ({ field, ignore_case: ignoreCase, op: 'starts_with', value });

export const between = <T, K extends keyof T>(field: K, lower: T[K], upper: T[K]): BetweenFilter<T, K> => ({
    field,
    lower,
    op: 'between',
    upper,
});
export const isIn = <T, K extends keyof T>(field: K, values: readonly T[K][]): ListFilter<T, K> => ({
    field,
    op: 'in',
    values,
});

export type FilterRequest = {
    readonly filter?: GeneralFilter;
    readonly paged?: PagedBodyParams;
    readonly sorted?: SortedBodyParams;
};
